import type { AttributeNode } from '@/types/schemas';
import type { JsonRecord } from '@/domain/entities/property-schema';

export type SchemaValidationLog = {
  missingRequired: string[];
  typeMismatches: Array<{ path: string; expected: string; actual: string }>;
  enumMismatches: Array<{ path: string; allowed: string[]; actual: string }>;
};

const isRecord = (v: unknown): v is JsonRecord => typeof v === 'object' && v != null && !Array.isArray(v);

const typeOf = (v: unknown) => (v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v);

const isEmptyValue = (v: unknown) =>
  v == null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

function pushMissing(log: SchemaValidationLog, path: string) {
  if (!log.missingRequired.includes(path)) log.missingRequired.push(path);
}

export function validateTemplateDataAgainstSchema({
  schemaBlocks,
  templateData,
}: {
  schemaBlocks: AttributeNode[];
  templateData: unknown;
}): SchemaValidationLog {
  const log: SchemaValidationLog = {
    missingRequired: [],
    typeMismatches: [],
    enumMismatches: [],
  };

  const walkNode = (node: AttributeNode, value: unknown, path: string) => {
    // required: must exist + not be empty (string/array)
    if (node.required && isEmptyValue(value)) {
      pushMissing(log, path);
      // still continue only if object/array exists; otherwise no deeper validation possible
      if (value == null) return;
    }

    // null handling
    if (value === null) {
      if (!node.nullable) {
        log.typeMismatches.push({ path, expected: node.type, actual: 'null' });
      }
      return;
    }

    // type checks
    if (value !== undefined) {
      switch (node.type) {
        case 'string': {
          if (typeof value !== 'string') {
            log.typeMismatches.push({ path, expected: 'string', actual: typeOf(value) });
            return;
          }
          if (node.enum?.length && !node.enum.includes(value)) {
            log.enumMismatches.push({ path, allowed: node.enum, actual: value });
          }
          return;
        }
        case 'number': {
          if (typeof value !== 'number' || !Number.isFinite(value)) {
            log.typeMismatches.push({ path, expected: 'number', actual: typeOf(value) });
          }
          return;
        }
        case 'boolean': {
          if (typeof value !== 'boolean') {
            log.typeMismatches.push({ path, expected: 'boolean', actual: typeOf(value) });
          }
          return;
        }
        case 'object': {
          if (!isRecord(value)) {
            log.typeMismatches.push({ path, expected: 'object', actual: typeOf(value) });
            return;
          }

          // validate children
          for (const child of node.attributes ?? []) {
            const childVal = (value as JsonRecord)[child.key];
            walkNode(child, childVal, `${path}.${child.key}`);
          }
          return;
        }
        case 'array': {
          if (!Array.isArray(value)) {
            log.typeMismatches.push({ path, expected: 'array', actual: typeOf(value) });
            return;
          }

          // validate items
          if (node.items) {
            value.forEach((itemVal, idx) => {
              // items has a key like "item" but we don't want it in the path
              walkNode(node.items!, itemVal, `${path}[${idx}]`);
            });
          }
          return;
        }
        default:
          return;
      }
    }
  };

  // root is always an object in your schemaToBlocks model: validate each top-level key
  const rootVal = isRecord(templateData) ? templateData : {};
  for (const b of schemaBlocks) {
    walkNode(b, (rootVal as JsonRecord)[b.key], b.key);
  }

  return log;
}

export function hasSchemaValidationIssues(log: SchemaValidationLog): boolean {
  return log.missingRequired.length > 0 || log.typeMismatches.length > 0 || log.enumMismatches.length > 0;
}
